import React, { useState, useEffect, useRef } from "react";
import SideMenuTabs from "./SideMenuTabs";
import logo from "../icons/gymx-final-black.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, Outlet, useLocation } from "react-router-dom";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import "./layout.css";
import MobileMenu from "./MobileMenu";
import {
  getSessionUser,
  isPlatformSuperAdmin,
  isSuperAdminPathAllowed,
  hasCompanyFeature,
} from "../utils/roleAccess";

function Main() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [mobileMenu, setMobileMenu] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [role, setRole] = useState("");
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const sessionStr = sessionStorage.getItem("user-info");
    if (sessionStr) {
      const session = JSON.parse(sessionStr);
      setUserName(session.user.name);
      setUserEmail(session.user.email);
      setRole(session.user.role);
    } else {
      navigate("/login");
    }
  }, [navigate]);

  useEffect(() => {
    if (!role) return;
    if (isPlatformSuperAdmin(role) && !isSuperAdminPathAllowed(location.pathname)) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (!isPlatformSuperAdmin(role) && location.pathname === "/pos") {
      const user = getSessionUser();
      if (user && !hasCompanyFeature(user, "pos")) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [role, location.pathname, navigate]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isMenuOpen]);

  const handleOpenMenu = (event) => {
    event.stopPropagation();
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLogout = () => {
    sessionStorage.removeItem("user-info");
    localStorage.removeItem("user-info");
    sessionStorage.removeItem("member-portal-auth");
    localStorage.removeItem("member-portal-auth");
    sessionStorage.removeItem("tab-index");
    navigate("/login");
  };

  const toggleSidebarMenu = () => setIsSidebarOpen(!isSidebarOpen);
  const handleMobileMenu = () => setMobileMenu(!mobileMenu);

  return (
    <div className="min-h-[100dvh] bg-gx-canvas">
      <header className="flex h-16 items-center print:hidden sticky top-0 z-nav bg-gx-surface border-b border-gx-border">
        <button
          type="button"
          aria-label={mobileMenu ? "Close menu" : "Open menu"}
          className="flex h-11 w-11 items-center justify-center text-gx-navy sm:hidden ml-2 rounded-gx-control hover:bg-gx-canvas"
          onClick={handleMobileMenu}
        >
          <FontAwesomeIcon icon={mobileMenu ? faTimes : faBars} className="text-xl" />
        </button>
        <button
          type="button"
          aria-label={isSidebarOpen ? "Collapse sidebar" : "Expand sidebar"}
          className="hidden sm:flex h-11 w-11 items-center justify-center text-gx-navy ml-2 rounded-gx-control hover:bg-gx-canvas"
          onClick={toggleSidebarMenu}
        >
          <FontAwesomeIcon icon={faBars} className="text-xl" />
        </button>
        <button type="button" className="px-3" onClick={() => navigate("/")}>
          <img src={logo} alt="GymXS" className="h-8 w-auto cursor-pointer" />
        </button>
        <div className="ml-auto relative pr-3" ref={menuRef}>
          <button
            type="button"
            aria-label="Account menu"
            aria-expanded={isMenuOpen}
            className="flex h-11 w-11 items-center justify-center rounded-gx-control text-gx-ink-soft hover:bg-gx-canvas"
            onClick={handleOpenMenu}
          >
            <AccountCircleOutlinedIcon style={{ fontSize: 28 }} />
          </button>
          {isMenuOpen && (
            <div className="absolute right-0 top-12 w-64 rounded-gx border border-gx-border bg-gx-surface shadow-gx p-2 z-popup">
              <div className="px-3 py-2">
                <p className="font-semibold text-gx-ink truncate">{userName}</p>
                <p className="text-sm text-gx-ink-soft truncate">{userEmail}</p>
                {role ? (
                  <p className="text-xs text-gx-ink-soft mt-1 capitalize">{role}</p>
                ) : null}
              </div>
              <div className="h-px bg-gx-border my-1" />
              <button
                type="button"
                className="w-full text-left px-3 py-2 rounded-gx-control text-sm font-medium text-gx-ink hover:bg-gx-canvas"
                onClick={() => navigate("/change-password")}
              >
                Change Password
              </button>
              {["admin", "clubadmin"].includes((role || "").toLowerCase()) && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-gx-control text-sm font-medium text-gx-ink hover:bg-gx-canvas"
                  onClick={() => navigate("/business-settings")}
                >
                  Business Settings
                </button>
              )}
              {(role || "").toLowerCase() === "admin" && (
                <button
                  type="button"
                  className="w-full text-left px-3 py-2 rounded-gx-control text-sm font-medium text-gx-ink hover:bg-gx-canvas"
                  onClick={() => navigate("/settings")}
                >
                  Settings
                </button>
              )}
              <div className="h-px bg-gx-border my-1" />
              <button
                type="button"
                className="w-full text-left px-3 py-2 rounded-gx-control text-sm font-medium text-gx-red hover:bg-gx-red/10"
                onClick={handleLogout}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <div className="sm:flex min-h-[calc(100dvh-4rem)]">
        <aside
          className={`hidden sm:block print:hidden bg-gx-navy transition-[width] duration-300 ${
            isSidebarOpen ? "w-60" : "w-[72px]"
          }`}
        >
          <SideMenuTabs
            isSidebarOpen={isSidebarOpen}
            toggleSidebar={toggleSidebarMenu}
          />
        </aside>
        <main className="flex-1 min-w-0 bg-gx-canvas p-4 md:p-6 print:p-0">
          <Outlet />
        </main>
      </div>

      {mobileMenu && <MobileMenu handleMobileMenu={handleMobileMenu} />}
    </div>
  );
}

export default Main;
